
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface Module {
  id: string;
  title: string;
  description: string | null;
  order_index: number;
  course_lessons: {
    id: string;
    title: string;
    description: string | null;
    order_index: number;
  }[];
}

interface CoursePreview {
  modules: Module[];
  resourcesCount: number;
}

export function useCourseEnrollment() {
  const [isEnrolled, setIsEnrolled] = useState<boolean | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [coursePreview, setCoursePreview] = useState<CoursePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCourseAvailable, setIsCourseAvailable] = useState(false);
  const [courseReleaseDate, setCourseReleaseDate] = useState<string | null>(null);
  const [introLessonId, setIntroLessonId] = useState<string | null>(null);
  const [purchaserEmail, setPurchaserEmail] = useState<string | null>(null);
  
  // Check user auth and enrollment status
  const checkEnrollment = useCallback(async (sessionId?: string) => {
    try {
      setError(null);
      const { data } = await supabase.auth.getSession();
      
      if (data.session) {
        setUser(data.session.user);
        const { data: enrollmentData, error } = await supabase.functions.invoke('verify-payment');
        
        if (error) {
          console.error("Error in verify-payment function:", error);
          throw new Error(error.message || "Failed to check enrollment status");
        }
        
        setIsEnrolled(enrollmentData.enrolled);
        setIsPending(enrollmentData.pending || false);
        setCoursePreview(enrollmentData.coursePreview || null);
        setIsCourseAvailable(enrollmentData.isCourseAvailable || false);
        setCourseReleaseDate(enrollmentData.courseReleaseDate || null);
        setIntroLessonId(enrollmentData.introLessonId || null);
        if (enrollmentData.email) {
          setPurchaserEmail(enrollmentData.email);
        }
      } else if (sessionId) {
        // For guest users, verify with session ID
        const { data: verifyData, error } = await supabase.functions.invoke('verify-payment', {
          body: { sessionId }
        });
        
        if (error) {
          console.error("Error verifying payment:", error);
        } else {
          setIsEnrolled(verifyData.enrolled);
          setIsPending(verifyData.pending || false);
          setIsCourseAvailable(verifyData.isCourseAvailable || false);
          setCourseReleaseDate(verifyData.courseReleaseDate || null);
          setIntroLessonId(verifyData.introLessonId || null);
          if (verifyData.email) {
            setPurchaserEmail(verifyData.email);
          }
        }
      } else {
        // Get intro lesson ID for non-authenticated users
        const { data: introData, error } = await supabase.functions.invoke('verify-payment', {
          body: { getPublicInfo: true }
        });
        
        if (!error && introData) {
          setIsCourseAvailable(introData.isCourseAvailable || false);
          setCourseReleaseDate(introData.courseReleaseDate || null);
          setIntroLessonId(introData.introLessonId || null);
        }
      }
    } catch (error: any) {
      console.error("Error checking enrollment:", error);
      setError(error.message || "An error occurred while checking course enrollment");
      toast({
        title: "Error checking enrollment",
        description: error.message || "Please try again later",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    // Check URL parameters for Stripe session ID
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    checkEnrollment(sessionId || undefined);
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      // Use setTimeout to prevent potential deadlocks with Supabase auth
      setTimeout(() => {
        checkEnrollment();
      }, 0);
    });
    
    return () => subscription.unsubscribe();
  }, [checkEnrollment]);

  // Function to initiate checkout - now supports both email and direct checkout
  const initiateCheckout = async (emailOrEvent?: React.MouseEvent<HTMLButtonElement> | string) => {
    try {
      if (emailOrEvent instanceof Event) {
        emailOrEvent.preventDefault();
      }
      
      setCheckoutLoading(true);
      
      // Show loading toast
      toast({
        title: "Preparing checkout...",
        description: "You'll be redirected to our secure payment page momentarily.",
      });
      
      const requestBody: any = {};
      if (typeof emailOrEvent === 'string') {
        requestBody.email = emailOrEvent;
      } else if (user?.email) {
        requestBody.email = user.email;
      }
      
      console.log("Initiating checkout with request body:", requestBody);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: requestBody
      });
      
      if (error) {
        console.error("Checkout error:", error);
        throw error;
      }
      
      if (data?.url) {
        // Redirect to Stripe checkout
        console.log("Redirecting to Stripe checkout URL:", data.url);
        window.location.href = data.url;
        return data;
      } else {
        throw new Error("No checkout URL returned from create-checkout function");
      }
    } catch (error: any) {
      console.error("Error creating checkout session:", error);
      toast({
        title: "Error",
        description: error.message || "Could not initiate payment process. Please try again.",
        variant: "destructive"
      });
      throw error;
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  // Function to refresh enrollment status
  const refreshEnrollmentStatus = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e instanceof Event) {
      e.preventDefault();
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Show toast
      toast({
        title: "Checking enrollment...",
        description: "Please wait while we verify your enrollment status.",
      });
      
      const { data: enrollmentData, error } = await supabase.functions.invoke('verify-payment');
      
      if (error) {
        throw new Error(error.message || "Failed to check enrollment status");
      }
      
      setIsEnrolled(enrollmentData.enrolled);
      setIsPending(enrollmentData.pending || false);
      setCoursePreview(enrollmentData.coursePreview || null);
      setIsCourseAvailable(enrollmentData.isCourseAvailable || false);
      setCourseReleaseDate(enrollmentData.courseReleaseDate || null);
      setIntroLessonId(enrollmentData.introLessonId || null);
      
      if (enrollmentData.email) {
        setPurchaserEmail(enrollmentData.email);
      }
      
      // Show success toast
      if (enrollmentData.enrolled) {
        toast({
          title: "Enrollment confirmed!",
          description: "You have access to the course.",
          variant: "default"
        });
      } else if (enrollmentData.pending) {
        toast({
          title: "Payment pending",
          description: "Your payment is still being processed.",
          variant: "default"
        });
      } else {
        toast({
          title: "Not enrolled",
          description: "You are not enrolled in this course.",
          variant: "default"
        });
      }
      
      return enrollmentData.enrolled;
    } catch (error: any) {
      console.error("Error refreshing enrollment status:", error);
      setError(error.message || "An error occurred while refreshing enrollment status");
      toast({
        title: "Error refreshing status",
        description: error.message || "Please try again later",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Format the course release date
  const formatReleaseDate = () => {
    return "December 1, 2025";
  };

  // Calculate days until release
  const daysUntilRelease = () => {
    const releaseDate = new Date("2025-12-01");
    const today = new Date();
    const diffTime = releaseDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  return {
    isEnrolled,
    isPending,
    loading,
    checkoutLoading,
    user,
    coursePreview,
    error,
    isCourseAvailable,
    courseReleaseDate,
    introLessonId,
    purchaserEmail,
    formatReleaseDate,
    daysUntilRelease,
    initiateCheckout,
    refreshEnrollmentStatus
  };
}
